import { useEffect, useMemo, useState } from 'react'

import PageNav from '../components/PageNav.jsx'
import { watchClubCourses } from '../lib/data.js'
import {
  calcCourseHandicapRaw,
  calcCourseHandicap,
  calcPlayingHandicap,
  strokesOnHole,
  isValidStrokeIndex,
  DEFAULT_STROKE_INDEX,
  ALLOWANCE_OPTIONS,
} from '../lib/handicapCalc.js'

// Standalone USGA course-handicap calculator. Self-contained: reads the saved
// club course catalog (read-only) for the optional course/tee picker, and uses
// the ported WHS formulas in ../lib/handicapCalc.js. It does NOT touch the live
// tournament scoring engine, leaderboard, or any tournament data.
const GREEN = '#123D26'
const CREAM = '#FFFFFF'
const GOLD = '#123D26'
const CRIMSON = '#B10C0C'
const FONT = 'Marcellus, Georgia, serif'
const HOLES = Array.from({ length: 18 }, (_, i) => i)

const emptyGross = () => Array(18).fill('')
let nextId = 1
const newPlayer = () => ({ id: nextId++, name: '', index: '', gross: emptyGross() })

export default function CalculatorApp() {
  const [courses, setCourses] = useState({})
  const [courseId, setCourseId] = useState('')
  const [teeName, setTeeName] = useState('')
  const [rating, setRating] = useState('71.0')
  const [slope, setSlope] = useState('113')
  const [par, setPar] = useState('72')
  const [allowance, setAllowance] = useState(100)
  const [si, setSi] = useState([...DEFAULT_STROKE_INDEX])
  const [players, setPlayers] = useState(() => [newPlayer(), newPlayer()])

  useEffect(() => watchClubCourses(setCourses), [])

  const courseList = useMemo(
    () => Object.entries(courses).map(([id, v]) => ({ id, ...(v || {}) }))
      .sort((a, b) => (a.courseName || '').localeCompare(b.courseName || '')),
    [courses],
  )
  const selectedCourse = courseList.find((c) => c.id === courseId) || null
  const tees = selectedCourse?.teeBoxes || []

  const siValid = isValidStrokeIndex(si.map((n) => Number(n)))

  // Apply a saved course: pull par + (optional) stroke index; leave tee choice
  // to fill rating/slope. Read-only — never writes back to the catalog.
  function applyCourse(id) {
    setCourseId(id)
    setTeeName('')
    const c = courseList.find((x) => x.id === id)
    if (!c) return
    if (Number.isFinite(Number(c.coursePar))) setPar(String(c.coursePar))
    const men = c.strokeIndexMen
    if (Array.isArray(men) && men.length === 18 && men.every((n) => Number.isInteger(n))) {
      setSi(men.map(Number))
    }
    if ((c.teeBoxes || []).length === 1) applyTee(c, c.teeBoxes[0].name)
  }
  function applyTee(course, name) {
    setTeeName(name)
    const t = (course?.teeBoxes || []).find((x) => x.name === name)
    if (!t) return
    if (Number.isFinite(Number(t.rating))) setRating(String(t.rating))
    if (Number.isFinite(Number(t.slope))) setSlope(String(t.slope))
    if (Number.isFinite(Number(t.par))) setPar(String(t.par))
  }

  const courseInput = { slopeRating: Number(slope), courseRating: Number(rating), par: Number(par) }
  const strokeIndex = si.map((n) => Number(n))

  const updatePlayer = (id, patch) => setPlayers((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  const setGross = (id, hole, val) =>
    setPlayers((ps) => ps.map((p) => (p.id === id ? { ...p, gross: p.gross.map((g, h) => (h === hole ? val : g)) } : p)))

  return (
    <div className="min-h-full" style={{ backgroundColor: GREEN, fontFamily: FONT }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
      <PageNav back home tone="gold" />
      <div className="max-w-5xl mx-auto px-4 pt-14 pb-12">
        <h1 className="text-3xl font-bold uppercase tracking-[0.2em] text-center mb-6" style={{ color: GOLD }}>
          Handicap Calculator
        </h1>

        {/* Course / tee / allowance */}
        <Card title="Course & Tee">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Saved course (optional)">
              <select className="calc-input" value={courseId} onChange={(e) => applyCourse(e.target.value)}>
                <option value="">— Manual entry —</option>
                {courseList.map((c) => <option key={c.id} value={c.id}>{c.courseName || '(unnamed)'}</option>)}
              </select>
            </Field>
            <Field label="Tee">
              <select className="calc-input" value={teeName} disabled={!selectedCourse}
                      onChange={(e) => applyTee(selectedCourse, e.target.value)}>
                <option value="">{selectedCourse ? '— Pick a tee —' : 'Manual'}</option>
                {tees.map((t) => <option key={t.name} value={t.name}>{t.name} · {t.rating}/{t.slope}</option>)}
              </select>
            </Field>
            <Field label="Course Rating"><input className="calc-input" inputMode="decimal" value={rating} onChange={(e) => setRating(e.target.value)} /></Field>
            <Field label="Slope"><input className="calc-input" inputMode="numeric" value={slope} onChange={(e) => setSlope(e.target.value)} /></Field>
            <Field label="Par"><input className="calc-input" inputMode="numeric" value={par} onChange={(e) => setPar(e.target.value)} /></Field>
            <Field label="Allowance">
              <select className="calc-input" value={allowance} onChange={(e) => setAllowance(Number(e.target.value))}>
                {ALLOWANCE_OPTIONS.map((a) => <option key={a} value={a}>{a}%</option>)}
              </select>
            </Field>
          </div>
        </Card>

        {/* Stroke index editor */}
        <Card title="Stroke Index (1 = hardest)"
              right={<button type="button" className="calc-btn-ghost" onClick={() => setSi([...DEFAULT_STROKE_INDEX])}>↺ Default</button>}>
          <div className="grid grid-cols-9 gap-1.5">
            {si.map((v, h) => (
              <div key={h} className="text-center">
                <div className="text-sm font-bold" style={{ color: `${GOLD}cc` }}>{h + 1}</div>
                <input
                  className="calc-si"
                  inputMode="numeric"
                  value={v}
                  onChange={(e) => setSi((cur) => cur.map((x, i) => (i === h ? e.target.value.replace(/\D/g, '') : x)))}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm font-bold" style={{ color: siValid ? '#2E7D32' : CRIMSON }}>
            {siValid ? '✓ Valid — each of 1–18 used once' : '⚠ Must be the numbers 1–18, each used exactly once'}
          </div>
        </Card>

        {/* Players + results */}
        <Card title="Players"
              right={<button type="button" className="calc-btn" onClick={() => setPlayers((ps) => [...ps, newPlayer()])}>+ Add player</button>}>
          <div className="space-y-4">
            {players.map((p) => (
              <PlayerBlock
                key={p.id}
                player={p}
                courseInput={courseInput}
                allowance={allowance}
                strokeIndex={strokeIndex}
                siValid={siValid}
                canRemove={players.length > 1}
                onChange={(patch) => updatePlayer(p.id, patch)}
                onGross={(hole, val) => setGross(p.id, hole, val)}
                onRemove={() => setPlayers((ps) => ps.filter((x) => x.id !== p.id))}
              />
            ))}
          </div>
        </Card>

        <p className="text-center text-sm mt-6" style={{ color: `${CREAM}88` }}>
          Read-only calculator · USGA WHS · Pine Bluff Country Club
        </p>
      </div>

      <style>{`
        .calc-input, .calc-si { width: 100%; border-radius: 8px; padding: 8px 10px; background: ${CREAM};
          color: ${GREEN}; border: 1px solid ${GOLD}66; font-family: ${FONT}; }
        .calc-si { padding: 6px 2px; text-align: center; font-weight: 700; }
        .calc-btn { background: ${GOLD}; color: ${GREEN}; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.08em; font-size: 12px; border-radius: 8px; padding: 6px 12px; }
        .calc-btn-ghost { color: ${GOLD}; border: 1px solid ${GOLD}66; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.08em; font-size: 11px; border-radius: 8px; padding: 5px 10px; }
      `}</style>
    </div>
  )
}

function Card({ title, right, children }) {
  return (
    <div className="rounded-2xl p-4 sm:p-5 mb-4" style={{ backgroundColor: CREAM, border: `1px solid ${GOLD}55` }}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold uppercase tracking-[0.14em]" style={{ color: GREEN }}>{title}</h2>
        {right || null}
      </div>
      {children}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(18, 61, 38,0.6)' }}>{label}</span>
      {children}
    </label>
  )
}

function PlayerBlock({ player, courseInput, allowance, strokeIndex, siValid, canRemove, onChange, onGross, onRemove }) {
  const idxNum = Number(player.index)
  const hasIdx = player.index !== '' && Number.isFinite(idxNum)
  const raw = hasIdx ? calcCourseHandicapRaw({ handicapIndex: idxNum, ...courseInput }) : null
  const ch = hasIdx ? calcCourseHandicap({ handicapIndex: idxNum, ...courseInput }) : null
  const ph = hasIdx ? calcPlayingHandicap(raw, allowance) : null
  // Strokes allocated off the PLAYING handicap (allowance honored).
  const strokes = HOLES.map((h) => (ph == null || !siValid ? 0 : strokesOnHole(ph, h, strokeIndex)))

  const grossNums = player.gross.map((g) => (g === '' ? null : Number(g)))
  const net = grossNums.map((g, h) => (g == null || !Number.isFinite(g) ? null : g - strokes[h]))
  const grossTotal = grossNums.reduce((s, g) => s + (Number.isFinite(g) ? g : 0), 0)
  const netTotal = net.reduce((s, n) => s + (Number.isFinite(n) ? n : 0), 0)
  const strokesTotal = strokes.reduce((s, x) => s + x, 0)

  return (
    <div className="rounded-xl p-3" style={{ border: `1px solid ${GOLD}44`, background: 'rgba(18, 61, 38,0.03)' }}>
      <div className="flex flex-wrap items-end gap-3 mb-2">
        <label className="flex-1 min-w-[140px]">
          <span className="block text-sm font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(18, 61, 38,0.6)' }}>Name</span>
          <input className="calc-input" value={player.name} placeholder="Player" onChange={(e) => onChange({ name: e.target.value })} />
        </label>
        <label className="w-24">
          <span className="block text-sm font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(18, 61, 38,0.6)' }}>Index</span>
          <input className="calc-input" inputMode="decimal" value={player.index} placeholder="14.2" onChange={(e) => onChange({ index: e.target.value })} />
        </label>
        <div className="flex items-center gap-4 pb-1.5">
          <Stat label="Course Hcp" value={ch} />
          <Stat label="Playing Hcp" value={ph} />
        </div>
        {canRemove ? (
          <button type="button" className="ml-auto pb-1.5 text-sm font-bold" style={{ color: CRIMSON }} onClick={onRemove}>Remove</button>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="text-center" style={{ minWidth: 640, color: GREEN }}>
          <thead>
            <tr className="text-sm uppercase" style={{ color: 'rgba(18, 61, 38,0.55)' }}>
              <th className="text-left pr-2 py-1">Hole</th>
              {HOLES.map((h) => <th key={h} className="px-1 py-1 w-8">{h + 1}</th>)}
              <th className="px-2 py-1">Tot</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            <tr>
              <td className="text-left pr-2 py-0.5 text-sm uppercase font-bold" style={{ color: 'rgba(18, 61, 38,0.55)' }}>Strokes</td>
              {strokes.map((s, h) => (
                <td key={h} className="px-1 py-0.5 font-bold" style={{ color: s > 0 ? CRIMSON : 'rgba(18, 61, 38,0.35)' }}>{s}</td>
              ))}
              <td className="px-2 py-0.5 font-bold" style={{ color: CRIMSON }}>{strokesTotal}</td>
            </tr>
            <tr>
              <td className="text-left pr-2 py-0.5 text-sm uppercase font-bold" style={{ color: 'rgba(18, 61, 38,0.55)' }}>Gross</td>
              {player.gross.map((g, h) => (
                <td key={h} className="px-0.5 py-0.5">
                  <input
                    className="calc-si"
                    inputMode="numeric"
                    value={g}
                    onChange={(e) => onGross(h, e.target.value.replace(/\D/g, ''))}
                  />
                </td>
              ))}
              <td className="px-2 py-0.5 font-bold">{grossTotal || '—'}</td>
            </tr>
            <tr>
              <td className="text-left pr-2 py-0.5 text-sm uppercase font-bold" style={{ color: 'rgba(18, 61, 38,0.55)' }}>Net</td>
              {net.map((n, h) => (
                <td key={h} className="px-1 py-0.5 font-bold" style={{ color: GREEN }}>{n == null ? '·' : n}</td>
              ))}
              <td className="px-2 py-0.5 font-extrabold" style={{ color: GREEN }}>{netTotal || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-sm font-bold uppercase tracking-widest" style={{ color: 'rgba(18, 61, 38,0.55)' }}>{label}</div>
      <div className="text-2xl font-extrabold leading-none" style={{ color: CRIMSON }}>{value == null ? '—' : value}</div>
    </div>
  )
}
